import { useForm, yupResolver } from "@mantine/form";
import {
  ExpirationMode,
  StartMode,
  Unit,
  useUpdateServiceAllotmentMutation,
  useDeleteServiceAllotmentMutation,
  GetAgreementQuery,
  useCreateServiceAllotmentMutation,
  CreateServiceAllotmentInput,
  useGetServiceAllotmentLazyQuery,
  ServiceFrequency,
  GetPackageQuery,
} from "src/generated/graphql";
import { memo } from "react";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import { Button, Group, useMantineTheme } from "@mantine/core";
import { GetPackage } from "src/api/graphql/queries/Package";
import { GetAgreement } from "src/api/graphql/queries/Agreement";
import { useEffect } from "react";
import { array, boolean, date, number, object, string } from "yup";
import ServiceAllotmentFormFields from "../formFields";

export interface IServiceAllotForm
  extends Omit<
    CreateServiceAllotmentInput,
    | "startMode"
    | "unit"
    | "value"
    | "locations"
    | "isPrivateSession"
    | "services"
    | "privateSessions"
    | "serviceFrequency"
  > {
  startMode: boolean;
  unit?: Unit;
  value?: number;
  locations: string[] | undefined;
  services: string[] | undefined;
  privateSessions: string[] | undefined;
  id?: string;
  serviceFrequency?: ServiceFrequency;
  isPrivateSession: boolean;
}

type IForm = NonNullable<
  GetAgreementQuery["agreementOne"]
>["initialAllotments"][0];
interface ServiceAllotmentDetailProps {
  formData: IForm;
  handleModal?: (allotment: IForm) => void;
  isModal?: boolean;
  pkgId?: string | undefined;
  agreementId?: string | undefined;
  isRecurring?: boolean;
  disabled?: boolean;
  isAllowWrite: boolean;
  isGenericPackage?: boolean;
  packageLocations?: NonNullable<GetPackageQuery["packageOne"]>["locations"];
}

const ServiceAllotmentDetail = ({
  formData,
  isModal,
  handleModal,
  pkgId,
  agreementId,
  isRecurring,
  disabled,
  isAllowWrite,
  isGenericPackage,
  packageLocations,
}: ServiceAllotmentDetailProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const refetchQuery = [
    {
      query: pkgId ? GetPackage : GetAgreement,
      variables: { id: pkgId ? pkgId : agreementId },
    },
  ];

  useEffect(() => {
    formData &&
      isModal &&
      getServiceAllotment({
        variables: { serviceAllotmentOneId: formData.id },
      });
  }, [formData, isModal]);

  const [getServiceAllotment] = useGetServiceAllotmentLazyQuery({
    onCompleted(data) {
      if (!data.serviceAllotmentOne) return;
      const {
        locations,
        services,
        startMode,
        expiration,
        startAt,
        privateSessions,
        ...rest
      } = data.serviceAllotmentOne;
      let _data = {
        locations: locations?.map((val) => val.id),
        services: services?.map((val) => val.id),
        privateSessions: privateSessions?.map((val) => val.id),
        isPrivateSession: !!privateSessions.length,
        startMode: startMode === StartMode.Date ? true : false,
        ...(expiration.expiresAt && {
          expiresAt: new Date(expiration.expiresAt),
        }),
        ...(startAt && {
          startAt: new Date(startAt),
        }),
        ...(expiration.expirationMode === ExpirationMode.Duration && {
          expirationUnit: expiration.unit,
          expirationValue: expiration.value,
        }),
        mode: expiration.expirationMode,
        ...rest,
      };
      form.setValues(_data);
      form.resetDirty(_data);
    },
    fetchPolicy: "network-only",
  });

  const [createServiceAllotment, { loading: createLoading }] =
    useCreateServiceAllotmentMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateServiceAllotment, { loading: updateLoading }] =
    useUpdateServiceAllotmentMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });
  const [deleteServiceAllotment, { loading: deleteLoading }] =
    useDeleteServiceAllotmentMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IServiceAllotForm>({
    initialValues: {
      name: "",
      description: "",
      locations: [],
      services: [],
      privateSessions: [],
      startMode: false,
      expiresAt: undefined,
      mode: ExpirationMode.Never,
      credits: 0,
      dailyLimit: 0,
      isPrivateSession: false,
    },
    validate: yupResolver(
      object().shape({
        name: string()
          .trim("No spaces allowed")
          .strict(true)
          .min(2, "Name should must be least 2 letters")
          .max(30, "Name must not be greater than 30 letters")
          .required("Name is required"),
        description: string().max(200).notRequired(),
        isPrivateSession: boolean(),
        services: array().when("isPrivateSession", {
          is: false,
          then: () =>
            array()
              .min(1, "at least 1 Service is required")
              .required("Services is required"),
        }),
        privateSessions: array().when("isPrivateSession", {
          is: true,
          then: () =>
            array()
              .min(1, "at least 1 Private Seesion is required")
              .required("Private Seesion is required"),
        }),
        locations: array()
          .min(1, "at least 1 Locations is required")
          .required("Locations is required"),
        dailyLimit: number().required("Daily Limit is required"),
        credits: number().required("Credits is required"),
        startMode: boolean(),
        startAt: date()
          .when("startMode", {
            is: true,
            then: () => date().required("First Allocation date is required"),
          })
          .nullable(),
        mode: string(),
        expiresAt: date()
          .when("mode", {
            is: ExpirationMode.Date,
            then: () => date().required("First Allocation date is required"),
          })
          .nullable(),
        expirationUnit: string().when("mode", {
          is: ExpirationMode.Duration,
          then: () => string().required("Unit is required"),
        }),
        expirationValue: number().when("mode", {
          is: ExpirationMode.Duration,
          then: () => number().required("Value is required"),
        }),
        serviceFrequency: isRecurring
          ? string().required("Frequency is required")
          : string().nullable(),
      })
    ),
  });

  const handleDelete = () => {
    deleteServiceAllotment({
      variables: { deleteServiceAllotmentId: formData?.id! },
    });
  };

  const handleSubmit = async (values: Required<IServiceAllotForm>) => {
    const {
      id,
      startMode,
      mode,
      startAt,
      expiresAt,
      expirationUnit,
      expirationValue,
      locations,
      services,
      isPrivateSession,
      privateSessions,
      ...restValues
    } = values;

    let allotmentValues: CreateServiceAllotmentInput = {
      startMode: startMode ? StartMode.Date : StartMode.Now,
      mode,
      ...(mode === ExpirationMode.Date && { expiresAt: new Date(expiresAt) }),
      ...(mode === ExpirationMode.Duration && {
        expirationUnit,
        expirationValue,
      }),
      startAt: startMode ? new Date(startAt) : null,
      locations: locations?.map((location) => ({ id: location })),
      services: isPrivateSession
        ? []
        : services?.map((service) => ({ id: service })),
      privateSessions: !isPrivateSession
        ? []
        : privateSessions?.map((sessionId) => ({ id: sessionId })),
      ...(formData?.id
        ? {}
        : pkgId
        ? { package: { id: pkgId } }
        : isRecurring
        ? {
            recurringAgreement: { id: agreementId! },
            serviceFrequency: values.serviceFrequency as ServiceFrequency,
          }
        : {
            initialAgreement: { id: agreementId! },
          }),
      ...restValues,
    };

    formData?.id
      ? updateServiceAllotment({
          variables: {
            data: allotmentValues,
            updateServiceAllotmentId: formData?.id,
          },
        })
      : createServiceAllotment({
          variables: {
            data: allotmentValues,
          },
        });
  };

  const handleEdit = () => {
    handleModal && handleModal(formData);
  };

  const title = `${formData?.name} \u00A0\u00A0\u00A0 ${formData?.credits}`;

  return (
    <>
      <FormCard
        title={title}
        loading={updateLoading || deleteLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) =>
          handleSubmit(values as Required<IServiceAllotForm>)
        )}
        hideCard={isModal}
        mb={10}
        size="md"
        radius={4}
        disabled={disabled}
      >
        {isModal ? (
          <>
            <ServiceAllotmentFormFields<IServiceAllotForm>
              form={form}
              isRecurring={isRecurring}
              isGenericPackage={isGenericPackage}
              packageLocations={packageLocations}
              isPackage={!!pkgId}
            />
            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={() => modals.closeAll()}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={createLoading || updateLoading}
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={createLoading || updateLoading}
              >
                {formData?.id ? "Save" : "Create"}
              </Button>
            </Group>
          </>
        ) : null}
      </FormCard>
    </>
  );
};

export default memo(ServiceAllotmentDetail);
